<template>
  <DialogPrime
    v-model:visible="visible"
    modal
    dismissable-mask
    position="top"
    :close-on-escape="true"
    class="mt-0"
    :style="{ width: '50rem' }"
    :breakpoints="{ '1199px': '75vw', '575px': '92vw' }"
    @show="resetSearch"
  >
    <template #container>
      <div ref="searchContainer" class="new-command-menu">
        <div class="p-3 flex gap-2">
          <InputGroup>
            <InputGroupAddon><i class="pi pi-search" /></InputGroupAddon>
            <InputText
              v-model="query"
              class="w-full"
              :placeholder="t('modules.commandMenu.v2.placeholder')"
            />
            <InputGroupAddon>
              <LISAButton
                :label="t('modules.commandMenu.v2.options')"
                outlined
                @click="optionsPopover?.toggle($event)"
              />
            </InputGroupAddon>
          </InputGroup>

          <Popover ref="optionsPopover">
            <GlobalSearchOptions
              v-model="selectedTypes"
              :available-types="availableTypes"
              :counts="counts"
            />
          </Popover>
        </div>

        <GlobalSearchResultList
          :results="quickResults"
          :selected-index="selectedIndex"
          max-height="65vh"
          @selected="openResult"
        />

        <div v-if="loading" class="px-3 py-2 text-color-secondary">
          {{ t("global.loading") }}
        </div>
        <div
          v-else-if="query.length >= 2 && quickResults.length === 0"
          class="px-3 py-4 text-center"
        >
          {{ t("modules.commandMenu.v2.noResults") }}
        </div>
        <LISAButton
          v-if="query.length >= 2"
          class="w-full justify-content-center border-noround border-x-none border-bottom-none"
          text
          :label="t('modules.commandMenu.v2.showAll')"
          @click="showAllResults"
        />
        <div class="p-2 text-sm text-color-secondary flex justify-content-between">
          <span>{{ t("modules.commandMenu.v2.minimumCharacters") }}</span>
          <span>↑ ↓ · Enter · Esc</span>
        </div>
      </div>
    </template>
  </DialogPrime>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from "vue";
import { useRouter } from "vue-router";
import DialogPrime from "primevue/dialog";
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from "primevue/inputgroupaddon";
import InputText from "primevue/inputtext";
import Popover from "primevue/popover";
import LISAButton from "@/components/shared/primevue/LISAButton.vue";
import { useAppTranslate } from "@/language/staticTranslations";
import GlobalSearchOptions from "../components/GlobalSearchOptions.vue";
import GlobalSearchResultList from "../components/GlobalSearchResultList.vue";
import {
  navigateToGlobalSearchResult,
  type GlobalSearchResult,
} from "../helper/GlobalSearchNavigation";
import { useGlobalSearch } from "../helper/useGlobalSearch";
import { useGlobalSearchPreferences } from "../helper/useGlobalSearchPreferences";

const visible = defineModel<boolean>({ required: true });
const { t } = useAppTranslate();
const router = useRouter();
const query = ref("");
const { selectedTypes, includeArchived, siteIds, sort, siteId } = useGlobalSearchPreferences();
const selectedIndex = ref(0);
const optionsPopover = useTemplateRef("optionsPopover");
const searchContainer = useTemplateRef<HTMLElement>("searchContainer");
const { results, availableTypes, counts, loading } = useGlobalSearch({
  query,
  selectedTypes,
  pageSize: 15,
  includeArchived,
  siteIds,
  sort,
});
const quickResults = computed(() => results.value.slice(0, 15));

watch(quickResults, () => {
  selectedIndex.value = quickResults.value.length ? 0 : -1;
});

function resetSearch() {
  query.value = "";
  selectedIndex.value = -1;
  void nextTick(() => searchContainer.value?.querySelector("input")?.focus());
}

async function openResult(result: GlobalSearchResult) {
  visible.value = false;
  await navigateToGlobalSearchResult(router, result);
}

async function showAllResults() {
  visible.value = false;
  await router.push({
    name: "GlobalSearchV2",
    query: {
      q: query.value,
      types: selectedTypes.value.join(","),
      archived: includeArchived.value ? undefined : "false",
      site: siteId.value == null ? undefined : String(siteId.value),
      sort: sort.value,
    },
  });
}

function keyHandler(event: KeyboardEvent) {
  if ((event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase() === "k") {
    event.preventDefault();
    visible.value = true;
    return;
  }
  if (!visible.value) return;
  if (event.key === "Escape") visible.value = false;
  if (event.key === "ArrowDown" && quickResults.value.length) {
    event.preventDefault();
    selectedIndex.value = (selectedIndex.value + 1) % quickResults.value.length;
  }
  if (event.key === "ArrowUp" && quickResults.value.length) {
    event.preventDefault();
    selectedIndex.value =
      (selectedIndex.value - 1 + quickResults.value.length) % quickResults.value.length;
  }
  if (event.key === "Enter" && quickResults.value[selectedIndex.value]) {
    event.preventDefault();
    void openResult(quickResults.value[selectedIndex.value]);
  }
}

onMounted(() => window.addEventListener("keydown", keyHandler, true));
onBeforeUnmount(() => window.removeEventListener("keydown", keyHandler, true));
</script>

<style scoped lang="scss">
.new-command-menu {
  background: var(--p-surface-0);
  border-radius: var(--p-border-radius-lg);
  overflow: hidden;
}
</style>
